<template>
  <div class="sstv-encoder">
    <div class="card">
      <div class="card-header">
        <h2>SSTV 图像编码</h2>
        <p>将图像转换为SSTV音频信号</p>
      </div>

      <div class="card-body">
        <!-- 操作面板 -->
        <div class="operation-panel">
          <div class="form-group">
            <label for="modeSelect">选择SSTV模式:</label>
            <select id="modeSelect" v-model="selectedMode" class="form-control">
              <option value="none" selected disabled hidden>请选择模式</option>
              <option value="none" disabled>--- Martin ---</option>
              <option value="M1">Martin M1</option>
              <option value="M2">Martin M2</option>
              <option value="none" disabled>--- Scottie ---</option>
              <option value="S1">Scottie 1</option>
              <option value="S2">Scottie 2</option>
              <option value="SDX">Scottie DX</option>
              <option value="none" disabled>--- WRAASE ---</option>
              <option value="WraaseSC2180">SC2-180</option>
              <option value="none" disabled>--- PD ---</option>
              <option value="PD50">PD-50</option>
              <option value="PD90">PD-90</option>
              <option value="PD120">PD-120</option>
              <option value="PD160">PD-160</option>
              <option value="PD180">PD-180</option>
              <option value="PD240">PD-240</option>
              <option value="PD290">PD-290</option>
            </select>

            <div class="mode-info" v-if="selectedMode !== 'none'">
              <div class="mode-badge">{{ getModeDisplayName() }}</div>
              <div class="mode-details">{{ getModeDetails() }}</div>
            </div>
          </div>

          <div class="form-group">
            <label for="imgPicker">上传图像:</label>
            <input type="file" id="imgPicker" @change="handleImageUpload" :disabled="selectedMode === 'none'" accept="image/*" class="form-control" />
          </div>

          <div class="preview-container" v-if="imageLoaded">
            <canvas ref="imgCanvas" class="preview-canvas"></canvas>
            <p class="warning-text" v-if="warningText">{{ warningText }}</p>
          </div>

          <div class="form-group" v-if="imageLoaded">
            <label for="callSign">呼号 (可选):</label>
            <input type="text" id="callSign" v-model="callSign" placeholder="呼号" class="form-control" @input="updatePreview" />

            <label for="callSignLocation">呼号位置:</label>
            <select id="callSignLocation" v-model="callSignLocation" class="form-control" @change="updatePreview">
              <option value="top-left">左上角</option>
              <option value="bottom-left">左下角</option>
            </select>
          </div>

          <div class="button-group">
            <button @click="encodeSSTV" :disabled="!canEncode" class="btn btn-primary">
              <i class="icon">🔊</i> 生成SSTV音频
            </button>
            <button @click="downloadWAV" :disabled="!canEncode" class="btn btn-secondary">
              <i class="icon">💾</i> 下载WAV文件
            </button>
          </div>

          <div v-if="isEncoding" class="status-container">
            <div class="spinner"></div>
            <p>正在生成SSTV音频...</p>
          </div>

          <audio v-if="audioUrl" controls :src="audioUrl" class="audio-player"></audio>
        </div>

        <!-- SSTV知识面板 -->
        <div class="info-panel">
          <div class="info-section">
            <h3>什么是SSTV?</h3>
            <p>慢扫描电视(Slow-Scan Television, SSTV)是一种用于通过无线电传输静态图像的方法。它最初由业余无线电爱好者开发，现在广泛用于太空通信、业余无线电和教育领域。</p>
            <p>SSTV信号可以通过普通的音频设备接收和发送，这使得它成为一种简单而有效的图像传输方式。</p>
          </div>

          <div class="info-section">
            <h3>SSTV模式</h3>
            <p>SSTV有多种传输模式，每种模式都有不同的特点和用途：</p>
            <ul class="mode-list">
              <li><strong>Martin</strong>: 提供良好的彩色图像质量，M1模式传输时间约为114秒。</li>
              <li><strong>Scottie</strong>: 由Eddie Murphy (GM3SBC)开发，S1模式传输时间约为110秒。</li>
              <li><strong>PD</strong>: 提供高分辨率图像，PD120模式是国际空间站(ISS)常用的模式。</li>
              <li><strong>Robot</strong>: 最早的SSTV彩色模式之一，Robot36需要36秒传输一张图像。</li>
            </ul>
          </div>

          <div class="info-section">
            <h3>使用提示</h3>
            <p>为获得最佳效果，请遵循以下建议：</p>
            <ul>
              <li>选择与目标接收设备兼容的SSTV模式</li>
              <li>使用分辨率匹配所选模式的图像</li>
              <li>确保图像具有良好的对比度和清晰度</li>
              <li>避免图像中的细节过多，因为SSTV的分辨率有限</li>
              <li>如果添加呼号，请确保其清晰可见</li>
            </ul>
          </div>

          <div class="info-section">
            <h3>历史与应用</h3>
            <p>SSTV技术自1950年代开始发展，最初用于业余无线电通信。如今，它在多个领域有着广泛应用：</p>
            <ul>
              <li><strong>太空通信</strong>: 国际空间站定期使用SSTV向地球发送图像</li>
              <li><strong>业余无线电</strong>: 全球的无线电爱好者使用SSTV交换图像</li>
              <li><strong>教育</strong>: 学校和大学使用SSTV进行科学教育</li>
              <li><strong>应急通信</strong>: 在自然灾害期间，SSTV可用于传输现场图像</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 导入SSTV编码相关的类和常量
import {
  MartinMOne, MartinMTwo,
  ScottieOne, ScottieTwo, ScottieDX,
  WraaseSC2180,
  PD50, PD90, PD120, PD160, PD180, PD240, PD290
} from '@/utils/sstv-encoder.js';

export default {
  name: 'SSTVEncoder',
  data() {
    return {
      selectedMode: 'none',
      imageLoaded: false,
      warningText: '',
      callSign: '',
      callSignLocation: 'top-left',
      audioUrl: null,
      isEncoding: false,
      audioCtx: null,
      rawImage: null,
      sstvFormat: null,
      canvasCtx: null,
      // 模式信息
      modeInfo: {
        'M1': { name: 'Martin M1', resolution: '320×256', time: '114秒', color: true, developer: 'Martin Emmerson', year: '1980s' },
        'M2': { name: 'Martin M2', resolution: '320×256', time: '58秒', color: true, developer: 'Martin Emmerson', year: '1980s' },
        'S1': { name: 'Scottie 1', resolution: '320×256', time: '110秒', color: true, developer: 'Eddie Murphy (GM3SBC)', year: '1980s' },
        'S2': { name: 'Scottie 2', resolution: '320×256', time: '71秒', color: true, developer: 'Eddie Murphy (GM3SBC)', year: '1980s' },
        'SDX': { name: 'Scottie DX', resolution: '320×256', time: '269秒', color: true, developer: 'Eddie Murphy (GM3SBC)', year: '1980s' },
        'PD50': { name: 'PD-50', resolution: '320×256', time: '50秒', color: true, developer: 'JA2BWH', year: '1990s' },
        'PD90': { name: 'PD-90', resolution: '320×256', time: '90秒', color: true, developer: 'JA2BWH', year: '1990s' },
        'PD120': { name: 'PD-120', resolution: '640×496', time: '120秒', color: true, developer: 'JA2BWH', year: '1990s', note: 'ISS常用模式' },
        'PD160': { name: 'PD-160', resolution: '512×400', time: '160秒', color: true, developer: 'JA2BWH', year: '1990s' },
        'PD180': { name: 'PD-180', resolution: '640×496', time: '180秒', color: true, developer: 'JA2BWH', year: '1990s' },
        'PD240': { name: 'PD-240', resolution: '640×496', time: '240秒', color: true, developer: 'JA2BWH', year: '1990s' },
        'PD290': { name: 'PD-290', resolution: '800×616', time: '290秒', color: true, developer: 'JA2BWH', year: '1990s' },
        'WraaseSC2180': { name: 'WRAASE SC2-180', resolution: '320×256', time: '180秒', color: true, developer: 'WRAASE', year: '1980s' }
      }
    }
  },
  computed: {
    canEncode() {
      return this.imageLoaded && this.selectedMode !== 'none';
    }
  },
  mounted() {
    // 创建音频上下文
    this.audioCtx = new (window.AudioContext || window.webkitAudioContext)();

    // 创建原始图像对象
    this.rawImage = new Image();
    this.rawImage.onload = this.updatePreview;
  },
  methods: {
    // 获取模式显示名称
    getModeDisplayName() {
      if (this.selectedMode === 'none') return '';
      return this.modeInfo[this.selectedMode].name;
    },

    // 获取模式详细信息
    getModeDetails() {
      if (this.selectedMode === 'none') return '';
      const info = this.modeInfo[this.selectedMode];
      let details = `分辨率: ${info.resolution} | 传输时间: ${info.time}`;
      if (info.note) details += ` | ${info.note}`;
      return details;
    },

    handleImageUpload(e) {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        this.rawImage.src = event.target.result;
        this.imageLoaded = true;
        this.warningText = '';
      };
      reader.readAsDataURL(file);
    },

    updatePreview() {
      if (!this.imageLoaded || !this.sstvFormat) return;

      const canvas = this.$refs.imgCanvas;
      this.canvasCtx = canvas.getContext('2d');

      // 设置画布尺寸
      canvas.width = this.sstvFormat.vertResolution;
      canvas.height = this.sstvFormat.numScanLines;

      // 绘制图像
      this.canvasCtx.drawImage(this.rawImage, 0, 0, canvas.width, canvas.height);

      // 添加呼号
      if (this.callSign) {
        this.canvasCtx.font = "bold 24pt sans-serif";

        let callSignYCord;
        if (this.callSignLocation === "top-left")
          callSignYCord = 30;
        else if (this.callSignLocation === "bottom-left")
          callSignYCord = this.sstvFormat.numScanLines - 6;

        this.canvasCtx.fillText(this.callSign, 10, callSignYCord);
        this.canvasCtx.strokeStyle = "white";
        this.canvasCtx.strokeText(this.callSign, 10, callSignYCord);
      }

      // 检查图像尺寸是否匹配模式
      const modeInfo = this.modeInfo[this.selectedMode];
      const [idealWidth, idealHeight] = modeInfo.resolution.split('×').map(Number);

      if (this.rawImage.width < idealWidth || this.rawImage.height < idealHeight) {
        this.warningText = `警告: 图像尺寸(${this.rawImage.width}×${this.rawImage.height})小于推荐尺寸(${idealWidth}×${idealHeight})，可能影响质量。`;
      } else {
        this.warningText = '';
      }
    },

    encodeSSTV() {
      if (!this.canEncode) return;

      this.isEncoding = true;

      // 获取画布数据
      const canvasData = this.canvasCtx.getImageData(0, 0, this.$refs.imgCanvas.width, this.$refs.imgCanvas.height);

      // 恢复音频上下文
      if (this.audioCtx.state === "suspended") {
        this.audioCtx.resume();
      }

      // 创建振荡器
      const oscillator = this.audioCtx.createOscillator();
      oscillator.type = "sine";
      oscillator.connect(this.audioCtx.destination);

      // 准备图像数据
      this.sstvFormat.prepareImage(canvasData.data);

      // 开始编码
      const startTime = this.audioCtx.currentTime + 1;
      oscillator.start(startTime);
      const endTime = this.sstvFormat.encodeSSTV(oscillator, startTime);
      oscillator.stop(endTime);

      this.isEncoding = false;
    },

    downloadWAV() {
      if (!this.canEncode) return;

      this.isEncoding = true;

      // 获取画布数据
      const canvasData = this.canvasCtx.getImageData(0, 0, this.$refs.imgCanvas.width, this.$refs.imgCanvas.height);

      // 计算SSTV信号持续时间
      const sstvSignalDuration = this.sstvFormat.getEncodedLength() + 1;

      // 创建离线音频上下文
      const offlineCtx = new OfflineAudioContext(1, 48000 * sstvSignalDuration, 48000);
      const oscillator = offlineCtx.createOscillator();
      oscillator.type = "sine";
      oscillator.connect(offlineCtx.destination);

      // 准备图像数据
      this.sstvFormat.prepareImage(canvasData.data);

      // 开始编码
      this.sstvFormat.encodeSSTV(oscillator, 1);
      oscillator.start(1);

      // 渲染音频
      offlineCtx.startRendering().then((audioBuffer) => {
        const audioData = audioBuffer.getChannelData(0);

        // 转换为Int16Array
        const audioLength = audioData.length;
        const audioInt16 = new Int16Array(audioLength);
        for (let i = 0; i < audioLength; i++) {
          audioInt16[i] = Math.max(-1, Math.min(1, audioData[i])) * 32767;
        }

        // 创建WAV文件
        const wavHeader = this.createWAVHeader(audioInt16.length * 2);
        const wavFile = new Uint8Array(wavHeader.byteLength + audioInt16.byteLength);
        wavFile.set(new Uint8Array(wavHeader), 0);
        wavFile.set(new Uint8Array(audioInt16.buffer), wavHeader.byteLength);

        // 创建Blob并下载
        const blob = new Blob([wavFile], { type: 'audio/wav' });
        this.audioUrl = URL.createObjectURL(blob);

        // 下载文件
        const a = document.createElement('a');
        a.href = this.audioUrl;
        a.download = 'sstv_signal.wav';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

        this.isEncoding = false;
      });
    },

    createWAVHeader(audioLength) {
      const headerSize = 44;
      const header = new ArrayBuffer(headerSize);
      const view = new DataView(header);

      // RIFF chunk
      view.setUint32(0, 0x52494646, false); // "RIFF"
      view.setUint32(4, 36 + audioLength, true); // File size
      view.setUint32(8, 0x57415645, false); // "WAVE"

      // fmt chunk
      view.setUint32(12, 0x666d7420, false); // "fmt "
      view.setUint32(16, 16, true); // Chunk size
      view.setUint16(20, 1, true); // Audio format (PCM)
      view.setUint16(22, 1, true); // Number of channels (mono)
      view.setUint32(24, 48000, true); // Sample rate
      view.setUint32(28, 48000 * 1 * 16 / 8, true); // Byte rate
      view.setUint16(32, 1 * 16 / 8, true); // Block align
      view.setUint16(34, 16, true); // Bits per sample

      // data chunk
      view.setUint32(36, 0x64617461, false); // "data"
      view.setUint32(40, audioLength, true); // Data size

      return header;
    }
  },
  watch: {
    selectedMode(newMode) {
      if (newMode === 'none') return;

      // 根据选择的模式创建相应的SSTV格式对象
      switch (newMode) {
        case 'M1': this.sstvFormat = new MartinMOne(); break;
        case 'M2': this.sstvFormat = new MartinMTwo(); break;
        case 'S1': this.sstvFormat = new ScottieOne(); break;
        case 'S2': this.sstvFormat = new ScottieTwo(); break;
        case 'SDX': this.sstvFormat = new ScottieDX(); break;
        case 'PD50': this.sstvFormat = new PD50(); break;
        case 'PD90': this.sstvFormat = new PD90(); break;
        case 'PD120': this.sstvFormat = new PD120(); break;
        case 'PD160': this.sstvFormat = new PD160(); break;
        case 'PD180': this.sstvFormat = new PD180(); break;
        case 'PD240': this.sstvFormat = new PD240(); break;
        case 'PD290': this.sstvFormat = new PD290(); break;
        case 'WraaseSC2180': this.sstvFormat = new WraaseSC2180(); break;
      }

      // 如果已经加载了图像，更新预览
      if (this.imageLoaded) {
        this.updatePreview();
      }
    }
  }
}
</script>

<style scoped>
.sstv-encoder {
  max-width: 800px;
  margin: 0 auto;
}

.card {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 20px;
}

.card-header {
  padding: 20px;
  background-color: #3a8c3a;
  color: white;
}

.card-header h2 {
  margin: 0;
  font-size: 1.5rem;
}

.card-header p {
  margin: 5px 0 0 0;
  opacity: 0.9;
  font-size: 0.95rem;
}

.card-body {
  padding: 20px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

/* 操作面板样式 */
.operation-panel {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* 信息面板样式 */
.info-panel {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.info-section {
  background-color: #f9f9f9;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.info-section h3 {
  color: #3a8c3a;
  margin-top: 0;
  margin-bottom: 10px;
  font-size: 1.1rem;
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.info-section p {
  color: #555;
  margin: 0 0 10px 0;
  line-height: 1.5;
  font-size: 0.95rem;
}

.info-section ul {
  margin: 0;
  padding-left: 20px;
}

.info-section li {
  margin-bottom: 5px;
  color: #555;
  font-size: 0.95rem;
}

.info-section strong {
  color: #333;
}

.mode-list li {
  margin-bottom: 8px;
}

.mode-info {
  margin-top: 10px;
  padding: 10px;
  background-color: #e8f5e9;
  border-radius: 4px;
  border-left: 4px solid #3a8c3a;
}

.mode-badge {
  font-weight: bold;
  color: #2e7d32;
  margin-bottom: 5px;
}

.mode-details {
  font-size: 0.9rem;
  color: #555;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: white;
  color: #333;
  font-size: 16px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: #3a8c3a;
  box-shadow: 0 0 0 2px rgba(58, 140, 58, 0.2);
}

.form-control:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.preview-container {
  margin: 15px 0;
  text-align: center;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 15px;
  background-color: #f9f9f9;
}

.preview-canvas {
  max-width: 100%;
  border: 1px solid #ddd;
  background-color: #f5f5f5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.warning-text {
  color: #ff5722;
  margin-top: 5px;
  font-weight: 500;
}

.button-group {
  display: flex;
  gap: 10px;
  margin-top: 20px;
}

.btn {
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  transition: background-color 0.2s, transform 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn .icon {
  margin-right: 5px;
}

.btn:hover:not(:disabled) {
  transform: translateY(-2px);
}

.btn:active:not(:disabled) {
  transform: translateY(0);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #3a8c3a;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #2e7d32;
}

.btn-secondary {
  background-color: #607d8b;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #546e7a;
}

.status-container {
  display: flex;
  align-items: center;
  margin-top: 15px;
  padding: 10px;
  background-color: #e8f5e9;
  border-radius: 4px;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top-color: #3a8c3a;
  animation: spin 1s ease-in-out infinite;
  margin-right: 10px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.audio-player {
  width: 100%;
  margin-top: 20px;
  border-radius: 4px;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .card-body {
    grid-template-columns: 1fr;
  }

  .card-header {
    padding: 15px;
  }

  .card-body {
    padding: 15px;
  }

  .form-control {
    padding: 8px 10px;
    font-size: 14px;
  }

  .button-group {
    flex-direction: column;
    gap: 8px;
  }

  .btn {
    width: 100%;
    padding: 10px;
    font-size: 14px;
  }
}

/* 超小屏幕优化 */
@media (max-width: 400px) {
  .card-header h2 {
    font-size: 1.3rem;
  }

  .card-header p {
    font-size: 0.85rem;
  }

  .form-control {
    padding: 6px 8px;
    font-size: 13px;
  }

  label {
    font-size: 13px;
  }

  .info-section h3 {
    font-size: 1rem;
  }

  .info-section p, .info-section li {
    font-size: 0.9rem;
  }
}

/* 深色模式适配 */
@media (prefers-color-scheme: dark) {
  .card {
    background-color: #1e1e1e;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
  }

  .card-header {
    background-color: #2e7d32;
  }

  label {
    color: #e0e0e0;
  }

  .form-control {
    background-color: #333;
    color: #e0e0e0;
    border-color: #444;
  }

  .form-control:focus {
    border-color: #7cb342;
    box-shadow: 0 0 0 2px rgba(124, 179, 66, 0.2);
  }

  .form-control:disabled {
    background-color: #252525;
  }

  .operation-panel, .info-section {
    background-color: #252525;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }

  .mode-info {
    background-color: rgba(76, 175, 80, 0.1);
    border-left-color: #4caf50;
  }

  .mode-badge {
    color: #81c784;
  }

  .mode-details {
    color: #bbb;
  }

  .preview-container {
    background-color: #252525;
    border-color: #444;
  }

  .preview-canvas {
    border-color: #444;
    background-color: #333;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  .btn-primary {
    background-color: #4caf50;
  }

  .btn-primary:hover:not(:disabled) {
    background-color: #43a047;
  }

  .btn-secondary {
    background-color: #546e7a;
  }

  .btn-secondary:hover:not(:disabled) {
    background-color: #455a64;
  }

  .status-container {
    background-color: rgba(76, 175, 80, 0.2);
  }

  .spinner {
    border-color: rgba(255, 255, 255, 0.1);
    border-top-color: #7cb342;
  }

  .warning-text {
    color: #ff8a65;
  }

  .info-section h3 {
    color: #81c784;
    border-bottom-color: #444;
  }

  .info-section p, .info-section li {
    color: #bbb;
  }

  .info-section strong {
    color: #e0e0e0;
  }
}
</style>
